/**
 * @fileoverview Floating Performance Dashboard (development only).
 * @description Aggregates slow lookup/query/render events emitted via
 * `perfBus` so developers can quickly identify the slowest paths. Renders
 * nothing in production builds.
 *
 * Usage: mount once near the app root. Toggle with the floating "" badge.
 */

import { useEffect, useMemo, useState } from 'react';
import { perfBus, aggregate, type PerfEvent, type PerfCategory } from '@/lib/perfBus';
import { Activity, X, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const isDev = import.meta.env.DEV;

const CATEGORY_COLORS: Record<PerfCategory, string> = {
  lookup: 'text-sky-600 dark:text-sky-400',
  render: 'text-amber-600 dark:text-amber-400',
  query: 'text-violet-600 dark:text-violet-400',
  other: 'text-muted-foreground',
};

function fmt(ms: number) {
  return `${Math.round(ms)}ms`;
}

export function PerfDashboard() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'aggregate' | 'recent'>('aggregate');
  const [filter, setFilter] = useState<'all' | 'slow'>('slow');
  const [events, setEvents] = useState<PerfEvent[]>([]);

  useEffect(() => {
    if (!isDev) return;
    return perfBus.subscribe(setEvents);
  }, []);

  if (!isDev) return null;

  const filtered = useMemo(
    () => (filter === 'slow' ? events.filter((e) => e.slow) : events),
    [events, filter]
  );
  const agg = useMemo(() => aggregate(filtered), [filtered]);
  const slowCount = useMemo(() => events.filter((e) => e.slow).length, [events]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-3 right-3 z-[9999] flex items-center gap-1.5 rounded-full border border-border bg-background/90 px-3 py-1.5 text-xs font-medium shadow-md backdrop-blur hover:bg-accent"
        title="Open performance dashboard"
      >
        <Activity className="h-3.5 w-3.5 text-primary" />
        <span></span>
        {slowCount > 0 && (
          <span className="ml-1 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
            {slowCount} slow
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-3 right-3 z-[9999] flex w-[420px] max-w-[95vw] flex-col rounded-lg border border-border bg-background shadow-xl">
      <header className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <Activity className="h-3.5 w-3.5 text-primary" />
          <span>Performance Dashboard</span>
          <span className="text-muted-foreground">· {events.length} events</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => perfBus.clear()}
            className="rounded p-1 text-muted-foreground hover:bg-accent"
            title="Clear"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setOpen(false)}
            className="rounded p-1 text-muted-foreground hover:bg-accent"
            title="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      <div className="flex items-center gap-1 border-b border-border px-2 py-1.5 text-[11px]">
        <button
          onClick={() => setTab('aggregate')}
          className={`rounded px-2 py-0.5 ${tab === 'aggregate' ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/50'}`}
        >
          Aggregate
        </button>
        <button
          onClick={() => setTab('recent')}
          className={`rounded px-2 py-0.5 ${tab === 'recent' ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/50'}`}
        >
          Recent
        </button>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => setFilter('all')}
            className={`rounded px-2 py-0.5 ${filter === 'all' ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/50'}`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('slow')}
            className={`rounded px-2 py-0.5 ${filter === 'slow' ? 'bg-amber-500/20 font-medium text-amber-700 dark:text-amber-300' : 'text-muted-foreground hover:bg-accent/50'}`}
          >
            Slow only
          </button>
        </div>
      </div>

      <div className="max-h-[50vh] overflow-auto">
        {tab === 'aggregate' ? (
          agg.length === 0 ? (
            <EmptyState />
          ) : (
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-muted/50 text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 font-medium">Path</th>
                  <th className="px-2 py-1 font-medium text-right">N</th>
                  <th className="px-2 py-1 font-medium text-right">avg</th>
                  <th className="px-2 py-1 font-medium text-right">max</th>
                </tr>
              </thead>
              <tbody>
                {agg.map((row) => (
                  <tr key={`${row.category}-${row.label}`} className="border-t border-border/60">
                    <td className="px-2 py-1">
                      <span className={`mr-1 text-[10px] uppercase ${CATEGORY_COLORS[row.category]}`}>
                        {row.category}
                      </span>
                      <span className="font-mono">{row.label}</span>
                      {row.slowCount > 0 && (
                        <span className="ml-1 rounded bg-amber-500/15 px-1 text-[9px] font-semibold text-amber-700 dark:text-amber-300">
                          {row.slowCount} slow
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">{row.count}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmt(row.avgMs)}</td>
                    <td className={`px-2 py-1 text-right font-mono ${row.maxMs > 800 ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}>
                      {fmt(row.maxMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="divide-y divide-border/60 text-[11px]">
            {filtered.slice(0, 100).map((e) => (
              <li key={e.id} className="flex items-center gap-2 px-2 py-1">
                <span className={`text-[10px] uppercase ${CATEGORY_COLORS[e.category]}`}>{e.category}</span>
                <span className="flex-1 truncate font-mono">{e.label}</span>
                <span className={`font-mono ${e.slow ? 'font-semibold text-amber-600 dark:text-amber-400' : ''}`}>
                  {fmt(e.durationMs)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <footer className="border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground">
        Dev only. Lookups &gt;800ms · renders &gt;100ms flagged slow.
      </footer>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-3 py-6 text-center text-[11px] text-muted-foreground">
      No events yet. Interact with the app to populate.
    </div>
  );
}

export default PerfDashboard;
